<template>
  <v-layout>
    <v-dialog :modelValue="dialog" @click:outside="$emit('updateDialog', false)" @keydown.esc="$emit('updateDialog', false)" max-width="1000px" transition="dialog-bottom-transition">
      <v-card>
        <v-form @submit.prevent ref="form" v-model="valid" v-on:submit.prevent="createItem">
          <v-card-title>
            <span class="headline">{{ modal_title }}</span>
          </v-card-title>

          <v-card-text>
            <!-- START FORM DIALOG -->
            <v-row align="center" justify="center" class="mb-4 mt-4">
              <v-col md="10" class="elevation-1 pt-4 pb-4" style="background: #f5f5f5">
                <a-steps :current="step">
                  <a-step v-for="item in steps" :key="item.title" :title="item.title" />
                </a-steps>
              </v-col>
            </v-row>

            <div class="steps-content">
              <!--step 0-->
              <v-row align="start" justify="center" v-if="step == 0" transition="fade-transition">
                <v-col md="10" class="mt-4 pr-0 pl-0">
                  <v-form ref="form_step1">
                    <div class="px-2 py-1 mb-3 bg-subtitle-section">
                      <p class="text-subtitle-1 mb-0"><b>Datos Generales</b></p>
                    </div>
                    <p>Complete los datos generales del producto</p>
                    <v-row>
                      <v-col md="4">
                        <v-text-field label="Código (SKU)*" name="sku" v-model="item.sku" :rules="[rules.required]" :disabled="disabled"></v-text-field>
                      </v-col>
                      <v-col md="8">
                        <v-text-field label="Nombre*" v-model="item.name" :disabled="disabled" :rules="[rules.required]"></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col md="6">
                        <v-combobox
                          v-model="item.client"
                          name="cliente"
                          item-title="name"
                          item-value="id"
                          :items="clients"
                          label="Cliente*"
                          autocomplete="off"
                          :disabled="disabled || isEdit()"
                          :rules="[rules.requiredComboBox]"
                          clearable
                        ></v-combobox>
                      </v-col>
                      <v-col md="6">
                        <v-combobox
                          v-model="item.suppliers"
                          name="proveedor"
                          item-title="name"
                          item-value="id"
                          :items="suppliers"
                          label="Proveedor"
                          autocomplete="off"
                          :disabled="disabled"
                          clearable
                          multiple
                          :rules="[rules.onlySelectFromListMultiple]"
                        ></v-combobox>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col md="6">
                        <v-combobox
                          v-model="item.category"
                          name="category"
                          item-title="name"
                          item-value="id"
                          :items="categories"
                          label="Rubro*"
                          autocomplete="off"
                          :disabled="disabled"
                          :rules="[rules.requiredComboBox]"
                          clearable
                          @update:modelValue="comboCategoryChange"
                        ></v-combobox>
                      </v-col>
                      <v-col md="6">
                        <v-combobox
                          v-model="item.subcategory"
                          name="subcategory"
                          item-title="name"
                          item-value="id"
                          :items="subcategories"
                          label="Subrubro"
                          autocomplete="off"
                          :disabled="disabled"
                          clearable
                          :rules="[rules.onlySelectFromList]"
                        ></v-combobox>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col md="6">
                        <v-combobox
                          v-model="item.product_type"
                          name="product_type"
                          item-title="name"
                          item-value="id"
                          :items="product_types"
                          label="Tipo de producto"
                          autocomplete="off"
                          :disabled="disabled"
                          clearable
                          :rules="[rules.onlySelectFromList]"
                        ></v-combobox>
                      </v-col>
                      <v-col md="3">
                        <v-text-field label="Stock minimo" name="stock_minimo" v-model="item.stock_min" :disabled="disabled"></v-text-field>
                      </v-col>
                      <v-col md="3">
                        <v-text-field label="Stock maximo" name="stock_maximo" v-model="item.stock_max" :disabled="disabled"></v-text-field>
                      </v-col>
                    </v-row>

                    <v-divider></v-divider>                

                    <v-row>
                      <v-col md="12" class="mb-0">                        
                        <div class="px-2 py-1 mb-3 bg-subtitle-section">
                          <p class="text-subtitle-1 mb-0">
                            <b>Atributos</b>                            
                          </p>
                        </div>
                      </v-col>

                      <!-- Alerta cuando el producto tiene stock -->
                      <v-col md="12" v-if="isBlockedByStock">
                        <v-alert
                          type="warning"
                          variant="tonal"
                          density="compact"
                          class="mb-3"
                        >
                          <div class="d-flex align-center">
                            <v-icon class="mr-2">mdi-lock</v-icon>
                            <span>Los atributos no pueden modificarse porque el producto tiene stock en el sistema. Para modificar estos campos, primero debe reducir el stock a cero.</span>
                          </div>
                        </v-alert>
                      </v-col>

                      <v-col md="12">
                        <v-checkbox density="compact" label="Es un producto kit" v-model="item.tiene_kit" :disabled="disabled || isEdit() || item.tiene_combo" @update:modelValue="onTieneKitChange" hint="Tildá esta opción si este producto está compuesto por varios artículos individuales que se venden o entregan juntos como un único conjunto." :persistent-hint="true"></v-checkbox>
                      </v-col>
                      <v-col md="12">
                        <v-checkbox 
                          density="compact" 
                          v-model="item.tiene_combo" 
                          :disabled="disabled || isEdit() || item.tiene_kit" 
                          @update:modelValue="onTieneComboChange" 
                          hint="Tildá esta opción si el producto se vende como un único SKU comercial, pero operativamente está compuesto por distintos artículos individuales"
                          :persistent-hint="true"
                        >
                          <template v-slot:label>
                            <span>Es un producto combo</span>
                            <v-tooltip location="top">
                              <template v-slot:activator="{ props }">
                                <v-icon v-bind="props" color="grey" class="ml-1">mdi-information</v-icon>
                              </template>
                              <span>
                                <p><b>Comportamiento en el WMS:</b></p>
                                  
                                  <p class="mb-0">Al ingresar una venta, el producto se desarma automáticamente.</p>
                                  <p class="mb-0">El stock se descuenta de cada artículo individual que compone el combo.</p>
                                  <p class="mb-0">El combo no existe físicamente en el depósito como unidad armada.</p>
                                  <p class="mb-0">El picking y la preparación se realizan por producto individual.</p>
                              </span>
                            </v-tooltip>
                          </template>
                        </v-checkbox>
                      </v-col>
                    </v-row>
                    <v-divider></v-divider>                    

                    <v-row>
                      <v-col md="4">
                        <v-checkbox density="compact" label="Tiene control por lote" v-model="item.tiene_control_por_lotes" :disabled="disabled || isBlockedByStock || item.tiene_combo"></v-checkbox>
                      </v-col>
                      <v-col md="4">
                        <v-checkbox density="compact" v-if="!item.tiene_kit" label="Tiene control por serie" v-model="item.tiene_control_por_serie" :disabled="disabled || isBlockedByStock || item.tiene_combo"></v-checkbox>
                      </v-col>
                      <v-col md="4">
                        <v-checkbox density="compact" label="Tiene control de calidad" v-model="item.tiene_control_de_calidad" :disabled="disabled || isBlockedByStock || item.tiene_combo"></v-checkbox>
                      </v-col>
                      <v-col md="4">
                        <v-checkbox density="compact" label="Requiere fecha de expiracion" v-model="item.requiere_fecha_expiracion" :disabled="disabled || isBlockedByStock || item.tiene_combo" @update:modelValue="onRequiereFechaExpiracionChange"></v-checkbox>
                      </v-col>
                      

                      <v-col md="4" v-if="isAliasEnabled('descriptivo_1')">
                        <v-checkbox density="compact" :label="getAliasControlLabel('descriptivo_1')" v-model="item.tiene_control_por_descriptivo_1" :disabled="disabled || isBlockedByStock || item.tiene_combo"></v-checkbox>
                      </v-col>
                      <v-col md="4" v-if="isAliasEnabled('descriptivo_2')">
                        <v-checkbox density="compact" :label="getAliasControlLabel('descriptivo_2')" v-model="item.tiene_control_por_descriptivo_2" :disabled="disabled || isBlockedByStock || item.tiene_combo"></v-checkbox>
                      </v-col>
                      <v-col md="4" v-if="isAliasEnabled('descriptivo_3')">
                        <v-checkbox density="compact" :label="getAliasControlLabel('descriptivo_3')" v-model="item.tiene_control_por_descriptivo_3" :disabled="disabled || isBlockedByStock || item.tiene_combo"></v-checkbox>
                      </v-col>
                      <v-divider></v-divider>
                    </v-row>

                    <v-row v-if="item.requiere_fecha_expiracion">
                      <v-col md="12">
                        <v-text-field 
                          label="Ventana de Expiracion" 
                          name="expiration_days_range" 
                          v-model="item.expiration_days_range" 
                          :disabled="disabled || isBlockedByStock"
                          hint="Ingresá cuántos días antes de la fecha de vencimiento el producto pasara a estado vencido."
                          persistent-hint
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-divider v-if="item.requiere_fecha_expiracion"></v-divider>

                    <v-row>
                      <v-col md="12" class="mb-0">                        
                        <div class="px-2 py-1 mb-3 bg-subtitle-section">
                          <p class="text-subtitle-1 mb-0">
                            <b>Reglas de picking</b>
                            <v-tooltip location="top">
                              <template v-slot:activator="{ props }">
                                <v-icon v-bind="props" color="grey" class="ml-1">mdi-information</v-icon>
                              </template>
                              <span>
                                <p>Las reglas de picking definen cómo se debe manejar las reservas y tareas de preparación en relación con las cantidades del inventario y algoritmo de selección del mismo.</p>
                              </span>
                            </v-tooltip>
                          </p>
                        </div>
                      </v-col>

                      <v-col md="4">
                        <v-combobox
                          v-model="item.tipo_rotacion"
                          name="tipo_rotacion"
                          item-title="name"
                          item-value="id"
                          :items="tipos_rotacion"
                          label="Tipo de rotacion*"
                          autocomplete="off"
                          :disabled="disabled || item.tiene_combo"
                          :rules="[rules.requiredComboBox]"
                          clearable
                        ></v-combobox>
                      </v-col>
                      <v-col md="4">
                        <v-combobox
                          v-model="item.frecuencia_rotacion"
                          name="frecuencia_rotacion"
                          item-title="name"
                          item-value="id"
                          :items="frecuencias_rotacion"
                          label="Frecuencia de rotacion*"
                          autocomplete="off"
                          :disabled="disabled || item.tiene_combo"
                          :rules="[rules.requiredComboBox]"
                          clearable
                        ></v-combobox>
                      </v-col>
                      <v-col md="4">
                        <v-combobox
                          v-model="item.storage_unit"
                          name="storage_unit"
                          item-title="name"
                          item-value="id"
                          :disabled="disabled || isEdit() || item.tiene_combo"
                          :items="storage_units"
                          label="Uni. de almacenamiento*"
                          autocomplete="off"
                          :rules="[rules.requiredComboBox]"
                        ></v-combobox>
                      </v-col>
                    
                      <v-col md="12">
                        <v-checkbox density="compact" label="No aplicar límite de picking" v-model="item.no_use_pallet_limit_in_picking" :disabled="disabled || item.tiene_combo" hint="Si está activo, el sistema no considerará la cantidad máxima de unidades o cajas configurada por pallet al generar el picking." :persistent-hint="true"></v-checkbox>
                      </v-col>

                      <v-col md="12">
                        <v-checkbox density="compact" label="Usar tipo de rotacion estricto" v-model="item.use_strict_rotation_type" :disabled="disabled || item.tiene_combo" hint="Si está activo, el sistema no usara el reordenamiento de pallets para generar la mejor combinacion de cantidades para las tareas de preparacion." :persistent-hint="true"></v-checkbox>
                      </v-col>
                    </v-row>

                    <v-divider></v-divider>    

                    <v-row>
                      <v-col md="4">
                        <v-checkbox density="compact" label="Discontinuado" v-model="item.discontinuado" :disabled="disabled"></v-checkbox>
                      </v-col>
                      <v-col md="8">
                        <v-checkbox density="compact" label="Excluir de políticas de reposición" v-model="item.excluir_de_politicas_reposicion" :disabled="disabled" hint="Si está activo, las políticas de reposición automáticas no crean ni modifican reglas de reposición para este producto." :persistent-hint="true"></v-checkbox>
                      </v-col>
                    </v-row>                      
                  </v-form>
                  <v-btn class="float-right btn-second" v-if="step < steps.length - 1" @click="next">Siguiente</v-btn>
                </v-col>
              </v-row>

              <!--step 1-->
              <v-row align="start" justify="center" v-if="step == 1 && !item.tiene_combo" transition="fade-transition">
                <v-col md="10" class="mt-4 pr-0 pl-0">
                  <div class="px-2 py-1 mb-3 bg-subtitle-section">
                    <p class="text-subtitle-1 mb-0"><b>Presentación</b></p>
                  </div>
                  <p>Ingrese las presentaciones de los productos</p>

                  <TablePresentations :product="item" :open="dialog" :validator="$validator" :isViewModal="disabled" @addProduct="addProduct" @removeProduct="removeProduct" :isEdit="isEdit()" />

                  <v-btn v-if="step > 0" @click="prev" class="mt-5" color="blue-grey">Atras</v-btn>
                  <v-btn class="float-right mt-5 btn-second" v-if="step < steps.length - 1" @click="next">Siguiente</v-btn>
                </v-col>
              </v-row>

              <v-row align="start" justify="center" v-if="(step == 2 && item.tiene_kit) || (step == 1 && item.tiene_combo)" transition="fade-transition">
                <v-col md="10" class="mt-4 pr-0 pl-0">
                  <div class="px-2 py-1 mb-3 bg-subtitle-section">
                    <p class="text-subtitle-1 mb-0"><b>Composición del {{item.tiene_kit ? 'Kit' : 'Combo'}}</b></p>
                  </div>
                  <p>Defina las presentaciones que componen este {{item.tiene_kit ? 'kit' : 'combo'}}</p>

                  <TableCompositions :product="item" :open="dialog" :validator="$validator" :isViewModal="disabled" :isEdit="isEdit()" @addComposition="addComposition" @removeComposition="removeComposition" />

                  <v-btn v-if="step > 0" @click="prev" class="mt-5" color="blue-grey">Atras</v-btn>
                </v-col>
              </v-row>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="$emit('updateDialog', false)">Cerrar</v-btn>
            <v-btn color="success" text type="submit" :loading="loading" v-if="!disabled && isLastStep()">{{ formBtn }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <DialogYesNo
      :open="dialog_incompleto"
      title="Producto sin presentaciones"
      message="El producto se va a guardar como INCOMPLETO ¿Confirma que quiere guardarlo asi?"
      :loading="loading"
      @cancel="dialog_incompleto = false"
      @accept="confirmIncompleto"
    />
  </v-layout>
</template>

<script>
import Constant from "@/constants/sections";
import { generalService } from "@/libs/ws/generalService";
import { settingsService } from "@/libs/ws/settingsService";
import { clientService } from "@/libs/ws/clientService";
import { proveedorService } from "@/libs/ws/proveedorService";
import { rubroService } from "@/libs/ws/rubroService";
import { subrubroService } from "@/libs/ws/subrubroService";
import { productTypeService } from "@/libs/ws/productTypeService";
import ValidMixin from "@/mixins/ValidMixin.vue";
import AliasMixin from "@/mixins/AliasMixin.vue";
import TablePresentations from "./TablePresentations.vue";
import TableCompositions from "./TableCompositions.vue";
import DialogYesNo from "@/components/DialogYesNo.vue";
import { productoService } from "@/libs/ws/productoService";
export default {
  components: { TablePresentations, TableCompositions, DialogYesNo },
  mixins: [ValidMixin, AliasMixin],
  props: {
    service: Object,
    button_name: String,
    modal_title: String,
    page_title: String,
    formBtn: String,
    dialog: Boolean,
    updateDialog: Function,
    loadList: Function,
    editItem: Object,
    create_access: Boolean,
  },
  data() {
    return {
      show: false,
      disabled: false,
      categories: [],
      subcategories: [],
      clients: [],
      storage_units: [],
      suppliers: [],
      product_types: [],
      tipos_rotacion: [],
      frecuencias_rotacion: [],
      loading: false,
      dialog_incompleto: false,
      step: 0,
      item: {
        id: "",
        sku: "",
        name: "",
        client: null,
        stock_min: 0,
        stock_max: 0,
        suppliers: [],
        category: null,
        subcategory: null,
        product_type: null,
        tipo_rotacion: null,
        frecuencia_rotacion: null,
        discontinuado: false,
        excluir_de_politicas_reposicion: false,
        expiration_days_range: null,
        tiene_kit: false,
        no_use_pallet_limit_in_picking: false,
        use_strict_rotation_type: false,
        tiene_control_por_lotes: false,
        tiene_control_por_serie: false,
        tiene_control_de_calidad: false,
        tiene_control_por_descriptivo_1: false,
        tiene_control_por_descriptivo_2: false,
        tiene_control_por_descriptivo_3: false,
        storage_unit: null,
        requiere_fecha_expiracion: false,
        presentations: [],
        composite_kit: [],
      },      
      defaultItem: null,
    };
  },
  computed: {
    isBlockedByStock() {
      // Si el producto tiene stock, bloquear la edición de atributos
      return this.isEdit() && this.item.has_stock === true;
    },
    steps() {
      let baseSteps = [
        {
          title: "Datos Generales",
          content: "First-content",
        },
        {
          title: "Presentación",
          content: "Second-content",
        },
      ];

      console.log("tiene_combo", this.item.tiene_combo);
      if(this.item.tiene_combo){
        //Hay que ocultar el segundo paso 'Presentación'
        baseSteps = [
          {
            title: "Datos Generales",
            content: "First-content",
          },
        ];
      }

      if (this.item.tiene_kit || this.item.tiene_combo) {
        baseSteps.push({
          title: "Composición",
          content: "Third-content",
        });
      }
      console.log("steps", baseSteps);

      return baseSteps;
    }
  },
  watch: {
    dialog() {
      if (this.dialog) {
        this.disabled = this.formBtn == "";
        this.step = 0;

        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }

        if (this.editItem != null && this.editItem.id > 0) {
          this.item = JSON.parse(JSON.stringify(this.editItem));
          this.reloadPresentations();
        } else {
          if (this.defaultItem != null) this.item = JSON.parse(JSON.stringify(this.defaultItem));
        }
      }
    },
  },
  mounted() {    
    this.loadClientes();
    this.loadProveedores();
    this.loadCategorias();
    this.loadProductTypes();
    this.loadRotaciones();
    this.loadStorageUnits();
    this.defaultItem = JSON.parse(JSON.stringify(this.item));
  },
  methods: {
    isEdit() {
      return this.editItem ? true : false;
    },
    onTieneKitChange(value) {
      if (value) {
        this.item.tiene_control_por_serie = false;
      } else {    
        this.item.composite_kit = [];
      }
    },
    onTieneComboChange(value) {
      if (value) {
        this.item.tiene_kit = false;
        this.item.composite_kit = [];
        this.item.no_use_pallet_limit_in_picking = false;
        this.item.use_strict_rotation_type = false;
        this.item.tiene_control_por_lotes = false;
        this.item.tiene_control_por_serie = false;
        this.item.tiene_control_de_calidad = false;
        this.item.requiere_fecha_expiracion = false;
        this.item.tiene_control_por_descriptivo_1 = false;
        this.item.tiene_control_por_descriptivo_2 = false;
        this.item.tiene_control_por_descriptivo_3 = false;
        this.item.tipo_rotacion = this.tipos_rotacion[0] || null;
        this.item.frecuencia_rotacion = this.frecuencias_rotacion[0] || null;
        this.item.storage_unit =
          this.storage_units.find((u) => u.id === productoService.STORAGE_UNITS_ID) ||
          this.storage_units[0] ||
          null;
      } else {
        this.item.tipo_rotacion = null;
        this.item.frecuencia_rotacion = null;
        this.item.storage_unit = null;
      }
    },
    isLastStep() {
      return this.step == this.steps.length - 1;
    },
    prev() {
      this.step--;
    },
    async next() {
      if (this.step == 0) {
        const { valid } = await this.$refs.form_step1.validate();
        if (valid) {
          //Si es el primer paso, es valido, tiene_combo y es una creacion(id == 0)
          //=> Crear presentacion base Unidad
          if(this.item.tiene_combo && (!this.item.id || this.item.id == 0)) {
            this.item.presentations = [];
            this.item.presentations.push({
              id: 0,
              name: "Unidad",
              barcode: null,
              presentation_type: { id: productoService.PRESENTATION_TYPE_UNIDAD },
              composite_presentation: null,
              amount_composite_presentation: null,
              storage_unit: { id: productoService.STORAGE_UNITS_ID },
              weight_unit: null,
              weight: "",
              measure_unit: null,
              heigth: "",
              width: "",
              length: "",
              volume: "",
              sellable: true,
              barcodes_alias_arr: [],

            });
          }

          this.step++;
        }
      } else if (this.step < this.steps.length - 1) {
        this.step++;
      }
    },
    comboCategoryChange(event) {
      this.loadSubcategorias();
    },    
    loadStorageUnits() {
      generalService.request(Constant.SECTION_UNITS + "/list_storage_units").then((response) => {
        this.storage_units = response.result.list;
      });
    },
    reloadPresentations() {
      // products/list (usado para poblar la fila que abre este modal) pasa por una cache de
      // archivo con TTL de 1 hora; una edicion reciente de alias puede no estar reflejada ahi
      // todavia. products/presentations no usa esa cache, asi que se pide siempre fresco al
      // abrir el modal para no mostrar datos desactualizados (ej. alias de codigo de barra).
      generalService.request(Constant.SECTION_PRODUCTS + "/presentations", "POST", { product: this.item.id }).then((response) => {
        this.item.presentations = response.result.list;
      });
    },
    loadProductTypes() {
      productTypeService
        .filter()
        .then((response) => {
          this.product_types = response.result.list;
        })
        .catch((e) => {
          console.log("service.load error");
          console.log(e);
        });
    },
    loadClientes() {
      clientService
        .filter({})
        .then((response) => {
          this.clients = response.result.list;
        })
        .catch((e) => {
          console.log("service.load error");
          console.log(e);
        });
    },
    loadRotaciones() {
      productoService
        .tiposRotacoin()
        .then((response) => {
          this.tipos_rotacion = response.result;
        })
        .catch((e) => {
          console.log("service.load error");
          console.log(e);
        });
      productoService
        .frecuenciasRotacoin()
        .then((response) => {
          this.frecuencias_rotacion = response.result;
        })
        .catch((e) => {
          console.log("service.load error");
          console.log(e);
        });
    },
    loadProveedores() {
      proveedorService
        .filter({})
        .then((response) => {
          this.suppliers = response.result.list;
        })
        .catch((e) => {
          console.log("service.load error");
          console.log(e);
        });
    },
    loadCategorias() {
      rubroService
        .filter()
        .then((response) => {
          console.log("categorias", response.result);
          this.categories = response.result.list;
        })
        .catch((e) => {
          console.log("service.load error");
          console.log(e);
        });
    },
    loadSubcategorias() {
      subrubroService
        .filter({ category: this.item.category.id }, Constant.SECTION_SUBCATEGORIAS)
        .then((response) => {
          console.log("subcategorias", response.result);
          this.subcategories = response.result.list;
        })
        .catch((e) => {
          console.log("service.load error");
          console.log(e);
        });
    },
    addProduct(item, index = -1) {
      console.log("addProduct", item);
      if (index == -1) this.item.presentations.push(item);
      else this.item.presentations[index] = item;      
    },
    removeProduct(index) {
      this.item.presentations.splice(index, 1);
    },
    addComposition(item, index = -1) {
      const existing = this.item.composite_kit.find((kit, i) => 
        kit.component_product_presentation?.id === item.component_product_presentation?.id &&
        i !== index
      );

      if (existing) {
        this.toast.error("Ya existe una composición con esa presentación.");
        return;
      }
      if (index === -1) {
        this.item.composite_kit.push(item);
      } else {
        this.item.composite_kit[index] = item;
      }

    },
    removeComposition(index) {
      this.item.composite_kit.splice(index, 1);
    },
    validatePresentations(){
      //Sin presentaciones el producto se puede guardar igual, pero queda INCOMPLETO: se pide
      //confirmacion y el resto de las validaciones, que son sobre las presentaciones, no aplican.
      if(this.item.presentations.length == 0){
        //Solo los clientes con "Permitir productos incompletos" pueden guardar sin presentaciones
        if(!this.clientAllowsIncomplete()){
          this.toast.error("Debe ingresar al menos una presentacion");
          return false;
        }
        if(this.item.tiene_kit || this.item.tiene_combo){
          this.toast.error("Un " + (this.item.tiene_kit ? "kit" : "combo") + " no puede quedar sin presentaciones.");
          return false;
        }
        this.dialog_incompleto = true;
        return false;
      }

      //Debe existir al menos una presentacion base
      let count_base = 0;
      for (let i = 0; i < this.item.presentations.length; i++) {
        if(this.item.presentations[i].composite_presentation == null){
          count_base++;          
        }
      }
      if(count_base != 1){
        this.toast.error("Debe existir exactamente una presentacion base (sin composición)");
        return false;
      }

      //Debe existir al menos una presentacion de tipo Pallet
      let has_pallet = false;
      for (let i = 0; i < this.item.presentations.length; i++) {
        if(this.item.presentations[i].presentation_type && 
           this.item.presentations[i].presentation_type.name && 
           this.item.presentations[i].presentation_type.name.toLowerCase() === 'pallet'){
          has_pallet = true;
          break;
        }
      }
      if(!has_pallet && !this.item.tiene_combo){
        this.toast.error("Debe existir al menos una presentacion de tipo Pallet");
        return false;
      }

      if(this.item.tiene_kit && this.item.composite_kit.length == 0){
        this.toast.error("Debe ingresar al menos una composición para el kit");
        return false;
      }

      //Unicidad de codigos (principal + alias) entre todas las presentaciones del producto.
      //validateAlias del modal de presentacion solo ve una presentacion a la vez. El backend
      //sigue siendo la autoridad; esto es solo UX para evitar un viaje al server innecesario.
      const seen_codes = new Set();
      for (let i = 0; i < this.item.presentations.length; i++) {
        const presentation = this.item.presentations[i];
        const codes = [presentation.barcode, ...((presentation.barcodes_alias_arr || []).map(a => a.barcode))]
          .filter(c => c != null && String(c).trim() !== "")
          .map(c => String(c).trim());

        for (const code of codes) {
          if (seen_codes.has(code)) {
            this.toast.error("El codigo de barra '" + code + "' esta repetido entre las presentaciones del producto.");
            return false;
          }
          seen_codes.add(code);
        }
      }

      return true;
    },
    createItem() {
      if (this.loading) return;

      if (this.validatePresentations()) {
        this.saveItem();
      }
    },
    //El flag se toma de la lista de clientes del combo: el cliente del producto en edicion
    //puede venir sin el.
    clientAllowsIncomplete() {
      if (this.item.client == null) return false;
      const client = this.clients.find((c) => c.id == this.item.client.id) || this.item.client;
      return client.allow_incomplete_products === true;
    },
    //Confirmacion del alta sin presentaciones: el producto se guarda INCOMPLETO.
    confirmIncompleto() {
      this.dialog_incompleto = false;
      this.saveItem();
    },
    saveItem() {
      this.loading = true;

      var data = JSON.parse(JSON.stringify(this.item));
      data.presentations_arr = data.presentations;
      data.suppliers_arr = data.suppliers;
      data.composite_kit_arr = data.composite_kit;
      delete data.presentations;
      delete data.suppliers;
      delete data.composite_kit;
      if (this.item.id > 0) {
        this.service
          .update(data)
          .then((response) => {
            if (response.status) {
              this.toast.success("El " + this.modal_title + " se modifico correctamente.");
              this.$emit("updateDialog", false);
              this.step = 0;
              this.$emit("loadList");
            } else {
              if (Object.prototype.hasOwnProperty.call(response, "message")) {
                this.toast.error(response.message);
              } else {
                this.toast.error(response.msg);
              }
            }
          })
          .catch((response) => {
            this.toast.error(response.msg);
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.service
          .create(data)
          .then((response) => {
            if (response.status) {
              this.toast.success("El " + this.modal_title + " se creo correctamente");
              this.$emit("updateDialog", false);
              this.step = 0;
              this.$emit("loadList");
            } else {
              if (Object.prototype.hasOwnProperty.call(response, "message")) {
                this.toast.error(response.message);
              } else {
                this.toast.error(response.msg);
              }
            }
          })
          .catch((e) => {
            this.toast.error(e);
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
  onRequiereFechaExpiracionChange(){
          if(this.item.requiere_fecha_expiracion) {
            this.item.expiration_days_range = 1;
          } else {
            this.item.expiration_days_range = null;
          }
        }
    },
};
</script>
